import {
  CheckoutOptions,
  PaymentMethodToken,
  ResumeToken,
  SinglePaymentMethodCheckoutOptions,
  ThreeDSVerification,
  ThreeDSVerificationOptions,
} from '../types';
import ThreeDSecure from '../three-d-secure/ThreeDSecure';
import { PaymentInstrumentType, PaymentMethodType } from '../enums/Tokens';
import { ProgressEvent, ThreeDSecureStatus } from '../enums/Tokenization';
import { ClientContext } from '../core/ClientContextFactory';
import {
  createResumeSuccessCallback,
  createTokenizeSuccessCallback,
} from './createSuccessCallback';

import { IViewUtils } from './ui/types';
import { mergeTokenWithSynthetic3DSError } from './mergeTokenWithSynthetic3DSError';
import { ApiEvent } from '../analytics/constants/enums';
import { SuccessCallbackOptions } from '../core/ProgressNotifier';

import {
  ClientTokenIntent,
  DecodedClientToken,
} from '../core/ClientTokenHandler';
import { ErrorCode, PrimerClientError } from '../errors';
import { Analytics } from '../analytics/Analytics';
import ThreeDSUtils from './ui/ThreeDSUtils';

type TokenizationHandlers = Pick<
  CheckoutOptions,
  'onTokenizeStart' | 'onTokenizeError' | 'onTokenizeProgress' | 'onResumeError'
> & {
  onTokenizeSuccess: (data: PaymentMethodToken) => Promise<void>;
  onResumeSuccess: (data: ResumeToken) => Promise<void>;
};

const handleThreeDSAnalytics = (
  analytics: Analytics,
  verification: ThreeDSVerification,
) => {
  if (verification.error) {
    analytics.call({ event: ApiEvent.threeDSecureError });
  }

  if (verification.status === ThreeDSecureStatus.FAILED) {
    analytics.call({ event: ApiEvent.threeDSecureFailed });
  }

  if (verification.status === ThreeDSecureStatus.SKIPPED) {
    analytics.call({ event: ApiEvent.threeDSecureSkipped });
  }

  if (verification.status === ThreeDSecureStatus.SUCCESS) {
    analytics.call({ event: ApiEvent.threeDSecureSuccess });
  }
};

export const CheckoutTokenizationHandlers = {
  create(
    context: ClientContext,
    viewUtils: IViewUtils,
    options: CheckoutOptions | SinglePaymentMethodCheckoutOptions,
  ): TokenizationHandlers {
    let threeDS: ThreeDSecure | undefined;

    const verifyOptions: Partial<ThreeDSVerificationOptions> | undefined =
      options.threeDSecure;

    const hasCard = !!context.session.paymentMethods.find(
      (paymentMethod) => paymentMethod.type === PaymentMethodType.PAYMENT_CARD,
    );

    if (hasCard) {
      threeDS = ThreeDSUtils.setup(context, viewUtils);
    }

    const onResumeError = async (error: PrimerClientError) => {
      viewUtils.store.setIsLoading(false);
      viewUtils.store.setIsProcessing(false);

      viewUtils.store.setErrorMessage(
        viewUtils.store.getTranslations()?.tokenizationError ?? 'ERROR',
      );
      options.onResumeError?.(error);
    };

    const onResumeSuccess = async (data: ResumeToken) => {
      await createResumeSuccessCallback(
        options.onResumeSuccess,
        context,
        viewUtils,
        options,
        {},
        onResumeError,
      )(data);
    };

    return {
      onTokenizeProgress(ev) {
        switch (ev.type) {
          case ProgressEvent.TOKENIZE_STARTED:
            viewUtils.store.setIsLoading(true);
            viewUtils.store.setIsProcessing(true);
            break;
          case ProgressEvent.TOKENIZE_ERROR:
            viewUtils.store.setIsLoading(false);
            viewUtils.store.setIsProcessing(false);
            break;
          default:
            break;
        }
      },
      onTokenizeStart: options.onTokenizeStart,
      onTokenizeError: (error) => {
        const errorMessage = viewUtils.store.getTranslations()
          .tokenizationError;
        if (errorMessage) {
          viewUtils.store.setErrorMessage(errorMessage);
        }
        context.analytics.call({ event: ApiEvent.tokenizationError });
        options.onTokenizeError?.(error);
      },
      async onTokenizeSuccess(
        data: PaymentMethodToken,
        callOptions?: SuccessCallbackOptions,
      ) {
        const onSuccess = createTokenizeSuccessCallback(
          options.onTokenizeSuccess,
          context,
          viewUtils,
          options,
          {
            [ClientTokenIntent.THREEDS_AUTHENTICATION]: async () => {
              const decodedToken = context.clientTokenHandler.getCurrentDecodedClientToken() as DecodedClientToken;
              if (
                decodedToken.intent !== ClientTokenIntent.THREEDS_AUTHENTICATION
              )
                return;

              if (!threeDS) {
                return;
              }

              viewUtils.store.setIsLoading(true);
              viewUtils.store.setIsProcessing(true);

              try {
                const verification: ThreeDSVerification = await threeDS.verify({
                  token: decodedToken.tokenId,
                  ...verifyOptions,
                });

                handleThreeDSAnalytics(context.analytics, verification);

                onResumeSuccess({ resumeToken: verification.resumeToken });
              } catch (e) {
                onResumeError(
                  PrimerClientError.fromErrorCode(
                    ErrorCode.THREE_DS_AUTH_FAILED,
                    { message: 'Cannot perform threeDS' },
                  ),
                );
              }
            },
          },
          onResumeError,
        );

        if (data.paymentInstrumentType !== PaymentInstrumentType.CARD) {
          onSuccess(data, callOptions);
          context.analytics.call({
            event: ApiEvent.completedCheckout,
            data: { paymentMethod: data.paymentInstrumentType },
          });
          return;
        }

        if (
          !options.threeDSecure?.order ||
          !context.session.threeDSecureEnabled ||
          !threeDS
        ) {
          onSuccess(data);
          context.analytics.call({
            event: ApiEvent.completedCheckout,
            data: { paymentMethod: data.paymentInstrumentType },
          });
          return;
        }

        const verification: ThreeDSVerification = await threeDS.verify({
          token: data.token,
          ...verifyOptions,
        });
        handleThreeDSAnalytics(context.analytics, verification);

        if (verification.error) {
          onSuccess(mergeTokenWithSynthetic3DSError(data, verification.error));
          return;
        }

        context.analytics.call({
          event: ApiEvent.completedCheckout,
          data: { paymentMethod: data.paymentInstrumentType },
        });

        //TODO: verification.data will be available following next deploy
        onSuccess(verification.data || data);
      },
      onResumeError,
      onResumeSuccess,
    };
  },
};
